import { useState } from 'react';
import { ChevronsUpDown, PanelLeftOpen, Search, X } from 'lucide-react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

import { useMediaQuery } from '@/lib/hooks/useMediaQuery';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDashboardContext } from '@/stores/context/dashboard-layout-context';
import { useSidebarCollapseStore } from '@/stores/sidebar-collapse';
import { useStore } from '@/stores';
import { BREAKPOINTS } from '@/lib/constants/breakpoints';
import { SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from '@/lib/constants/sidebar';
import { BusinessLogo } from '@/components/custom/business-logo';
import { useAccountGroup } from '@/features/account-group/api/use-account-group';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { cn } from '@/lib/utils';

import Logo from '../shared/logo';

import SidebarItems from './sidebar-items';
import QuickLogout from './quick-logout';
import ModeSwitcher from './mode-switcher';

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const statusBadgeClass: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  INACTIVE: 'bg-gray-100 text-gray-500',
  PENDING: 'bg-yellow-100 text-yellow-700',
  DORMANT: 'bg-red-100 text-red-600',
  INCOMPLETE: 'bg-orange-100 text-orange-600',
};

const BusinessSwitchDialog = ({
  open,
  onOpenChange,
  isMainGroup,
}: {
  open: boolean;
  onOpenChange: (val: boolean) => void;
  isMainGroup: boolean;
}) => {
  const userProfile = useStore((state) => state.profile);
  const selectedBusiness = useStore((state) => state.selectedBusiness);
  const setSelectedBusiness = useStore((state) => state.setSelectedBusiness);
  const currentBusinessId = userProfile?.business.id ? String(userProfile.business.id) : '';
  const selectedBusinessId = selectedBusiness?.id ?? '';
  const activeId = selectedBusinessId || currentBusinessId;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: accountGroupData } = useAccountGroup(
    { page: 1, limit: 100, name: debouncedSearch || undefined },
    { enabled: isMainGroup && open },
  );

  const businesses = accountGroupData?.data?.businesses ?? [];
  const mainBusinessId = accountGroupData?.data?.main_business_id;
  const mainBusiness =
    accountGroupData?.data?.businesses?.find((b) => String(b.id) === String(mainBusinessId)) ??
    null;

  const handleSelect = (id: string, name: string) => {
    setSelectedBusiness({ id, name });
    setSearch('');
    onOpenChange(false);
    const isSubBusiness = mainBusinessId !== undefined && id !== String(mainBusinessId);
    const subBusinessRedirectPaths = ['/profile', '/manage-notifications', '/settings'];
    const subBusinessRedirectToDashboard = ['/account-group', '/group-commission'];
    const redirectToMerchantList = ['/merchant/create', '/merchant/'].filter((p) =>
      pathname.startsWith(p),
    );
    const redirectToPaymentLinkList =
      pathname.startsWith('/payment-link/create') ||
      (pathname.startsWith('/payment-link/') && pathname !== '/payment-link');
    if (subBusinessRedirectPaths.includes(pathname) && isSubBusiness) {
      void navigate({ to: '/business-profile' });
    } else if (
      subBusinessRedirectToDashboard.some((path) => pathname.startsWith(path)) &&
      isSubBusiness
    ) {
      void navigate({ to: '/dashboard' });
    } else if (redirectToMerchantList.length > 0 && pathname !== '/merchant') {
      void navigate({ to: '/merchant' });
    } else if (redirectToPaymentLinkList) {
      void navigate({ to: '/payment-link' });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        onOpenChange(val);
        if (!val) setSearch('');
      }}
    >
      <DialogContent className="flex h-[480px] flex-col gap-0 overflow-hidden px-5 py-7 sm:max-w-sm">
        {/* Title */}
        <DialogHeader className="shrink-0 pb-4">
          <DialogTitle className="text-xl font-semibold">Change Business</DialogTitle>
          <DialogDescription className="text-sm text-gray-400">
            Choose the business you want to manage. Data will be adjusted based on the selected
            business.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="shrink-0 pb-2">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search business..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Main Business + Pilihan Business — satu RadioGroup agar radio konsisten */}
        <RadioGroup
          value={activeId}
          onValueChange={(val) => {
            const biz = businesses.find((b) => String(b.id) === val);
            if (biz) handleSelect(String(biz.id), biz.name);
          }}
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          {/* Main Business — selalu tampil, shrink-0 */}
          {mainBusiness && (
            <div className="shrink-0 pb-2">
              <label
                htmlFor="biz-main"
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50',
                  activeId === String(mainBusiness.id) && 'bg-blue-50 hover:bg-blue-50',
                )}
              >
                <RadioGroupItem
                  id="biz-main"
                  value={String(mainBusiness.id)}
                  className="shrink-0"
                />
                {/* Avatar initials */}
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                  {getInitials(mainBusiness.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'truncate text-sm font-medium',
                        activeId === String(mainBusiness.id) ? 'text-blue-700' : 'text-gray-700',
                      )}
                    >
                      {mainBusiness.name}
                    </span>
                    {mainBusiness.status && (
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                          statusBadgeClass[mainBusiness.status] ?? 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {mainBusiness.status}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">Main Business</span>
                </div>
              </label>
            </div>
          )}

          {/* Divider */}
          <div className="shrink-0 border-t" />

          {/* Pilihan Business — scrollable */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {businesses.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-gray-400">Business not found</p>
              </div>
            ) : (
              <div className="gap-0 py-2">
                {businesses.map((biz) => {
                  const isSelected = activeId === String(biz.id);
                  const isMain = String(biz.id) === String(mainBusinessId);
                  return (
                    <label
                      key={biz.id}
                      htmlFor={`biz-${biz.id}`}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50',
                        isSelected && 'bg-blue-50 hover:bg-blue-50',
                      )}
                    >
                      <RadioGroupItem
                        id={`biz-${biz.id}`}
                        value={String(biz.id)}
                        className="shrink-0"
                      />
                      {/* Avatar initials */}
                      <div
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                          isSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600',
                        )}
                      >
                        {getInitials(biz.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              'truncate text-sm font-medium',
                              isSelected ? 'text-blue-700' : 'text-gray-700',
                            )}
                          >
                            {biz.name}
                          </span>
                          {biz.status && (
                            <span
                              className={cn(
                                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                                statusBadgeClass[biz.status] ?? 'bg-gray-100 text-gray-500',
                              )}
                            >
                              {biz.status}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {isMain ? 'Main Business' : 'Sub Business'}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </RadioGroup>
      </DialogContent>
    </Dialog>
  );
};

const BusinessSelector = ({ isCollapsed = false }: { isCollapsed?: boolean }) => {
  const userProfile = useStore((state) => state.profile);
  const selectedBusiness = useStore((state) => state.selectedBusiness);
  const isMainGroup =
    userProfile?.is_group === true && userProfile?.business_group_status === 'main';

  const currentBusinessId = userProfile?.business.id ? String(userProfile.business.id) : '';
  const selectedBusinessId = selectedBusiness?.id ?? '';
  const activeId = selectedBusinessId || currentBusinessId;
  const [open, setOpen] = useState(false);

  const { data: accountGroupData } = useAccountGroup(
    { page: 1, limit: 100 },
    { enabled: isMainGroup },
  );

  const businesses = accountGroupData?.data?.businesses ?? [];
  const mainBusinessId = accountGroupData?.data?.main_business_id;
  const selectedBiz = businesses.find((b) => String(b.id) === activeId);
  const displayName =
    selectedBiz?.name || selectedBusiness?.name || userProfile?.business.name || '-';
  const isSelectedMain = selectedBiz
    ? String(selectedBiz.id) === String(mainBusinessId)
    : userProfile?.business.is_main_business;

  if (isCollapsed) {
    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid="sidebar-business-selector"
              className="flex w-full cursor-pointer items-center justify-center border-t bg-gray-50 py-2.5 hover:bg-gray-100"
              onClick={() => {
                if (isMainGroup) setOpen(true);
              }}
            >
              {userProfile && (
                <div
                  data-testid="sidebar-avatar-business-logo"
                  className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-white"
                >
                  <BusinessLogo model={userProfile.business.logo_path} />
                </div>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{displayName}</TooltipContent>
        </Tooltip>

        <BusinessSwitchDialog open={open} onOpenChange={setOpen} isMainGroup={isMainGroup} />
      </>
    );
  }

  return (
    <>
      <button
        data-testid="sidebar-business-selector"
        className="flex w-full cursor-pointer flex-row items-center justify-between gap-2 border-t bg-gray-50 px-4 py-2.5 hover:bg-gray-100"
        onClick={() => {
          if (isMainGroup) setOpen(true);
        }}
      >
        <div className="flex items-center gap-2">
          {userProfile && (
            <div
              data-testid="sidebar-avatar-business-logo"
              className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-white"
            >
              <BusinessLogo model={userProfile.business.logo_path} />
            </div>
          )}
          <div className="flex min-w-0 flex-col items-start">
            <span className="text-xs font-normal text-gray-400">
              {userProfile?.is_group === true && selectedBusiness != null
                ? isSelectedMain
                  ? 'Main Business'
                  : 'Sub Business'
                : 'Main Business'}
            </span>
            <span className="line-clamp-1 text-sm font-medium text-black">{displayName}</span>
          </div>
        </div>
        {isMainGroup && <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-400" />}
      </button>

      <BusinessSwitchDialog open={open} onOpenChange={setOpen} isMainGroup={isMainGroup} />
    </>
  );
};

const Sidebar = ({
  topOffset = 0,
  mobileTopOffset = 0,
}: {
  topOffset?: number;
  mobileTopOffset?: number;
}) => {
  const isMediumView = useMediaQuery(BREAKPOINTS.md);
  const { isOpenDrawer, toggleSidebar } = useDashboardContext();
  const isCollapsed = useSidebarCollapseStore((state) => state.isCollapsed);
  const toggleCollapsed = useSidebarCollapseStore((state) => state.toggleCollapsed);
  const userProfile = useStore((state) => state.profile);
  const selectedBusiness = useStore((state) => state.selectedBusiness);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isMainGroup =
    userProfile?.is_group === true && userProfile?.business_group_status === 'main';

  const currentBusinessId = userProfile?.business.id ? String(userProfile.business.id) : '';
  const activeId = selectedBusiness?.id ?? currentBusinessId;

  const { data: accountGroupData } = useAccountGroup(
    { page: 1, limit: 100 },
    { enabled: isMainGroup },
  );
  const mainBusinessId = accountGroupData?.data?.main_business_id;
  const businesses = accountGroupData?.data?.businesses ?? [];
  const selectedBiz = businesses.find((b) => String(b.id) === activeId);
  const isSelectedMain = selectedBiz
    ? String(selectedBiz.id) === String(mainBusinessId)
    : userProfile?.business.is_main_business;

  if (isMediumView) {
    return (
      <>
        <div
          className="fixed z-[50] flex flex-col overflow-hidden rounded-md bg-white shadow transition-[width] duration-200"
          style={{
            top: topOffset,
            height: `calc(100vh - ${topOffset}px - 20px)`,
            width: isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
          }}
        >
          <div className="flex h-full flex-col">
            {isCollapsed && (
              <div className="flex items-center justify-center border-b px-4 py-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={toggleCollapsed}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
                      data-testid="sidebar-collapse-toggle"
                      aria-label="Expand sidebar"
                    >
                      <PanelLeftOpen className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Expand sidebar</TooltipContent>
                </Tooltip>
              </div>
            )}
            <div className="scrollbar-thin scrollbar-thumb-primary-main scrollbar-track-gray-300 flex-1 overflow-y-auto pt-5 pb-6">
              <SidebarItems
                isCollapsed={isCollapsed}
                onToggleCollapse={isCollapsed ? undefined : toggleCollapsed}
              />
            </div>
            <ModeSwitcher isCollapsed={isCollapsed} />
            <BusinessSelector isCollapsed={isCollapsed} />
          </div>
        </div>
      </>
    );
  }

  return (
    <Drawer direction="left" open={isOpenDrawer}>
      <DrawerContent
        data-testid="drawer-sidebar-mobile"
        onInteractOutside={toggleSidebar}
        aria-describedby={undefined}
        className="data-[vaul-drawer-direction=left]:w-full data-[vaul-drawer-direction=left]:sm:max-w-none"
        style={{ top: mobileTopOffset }}
      >
        <DrawerTitle asChild>
          <div className="flex items-center justify-between px-4 pt-4">
            <Logo />
            <button
              type="button"
              onClick={toggleSidebar}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close sidebar"
              data-testid="drawer-sidebar-mobile-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </DrawerTitle>
        <SidebarItems />

        <ModeSwitcher />
        <QuickLogout
          isMainGroup={isMainGroup}
          isSelectedMain={isSelectedMain}
          onSwitchBusiness={() => setMobileOpen(true)}
        />
        <BusinessSwitchDialog
          open={mobileOpen}
          onOpenChange={setMobileOpen}
          isMainGroup={isMainGroup}
        />
      </DrawerContent>
    </Drawer>
  );
};

export default Sidebar;
